import { useTranslation } from "react-i18next";
import styled from "styled-components/macro";
import { EditButton } from "../Buttons/Buttons";
import { ClickableLink } from "../Typography/Typography";
import { HeaderContainer } from "../Layout/Layout";
import { LeadAssigneeDetails } from "../../util/useBusinessUnit";
import { useCountriesList } from "../../util/Locations";
import { convertUserToOption, formatDateTime } from "../../util/util";
import { useMemo } from "react";
import ReactTooltip from "react-tooltip";
import { LeadAssignee } from "../../types/types";
import { screenSize } from "../../theme";

const AssignmentContainer = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.resolve("spacing_lg")};

  @media ${screenSize.mLarge} {
    flex-direction: column;
  }

  padding: ${({ theme }) => theme.resolve("spacing_md")}
    ${({ theme }) => theme.resolve("spacing_md")}
    ${({ theme }) => theme.resolve("spacing_md")} 0;
  background-color: ${({ theme }) =>
    theme.resolve("color_bg_surface_standard")};
`;

const FlexContainer = styled.div<{ $hasAssignee?: boolean }>`
  flex: ${({ $hasAssignee }) => ($hasAssignee ? "0 0 280px" : "none")};
  @media ${screenSize.mLarge} {
    flex: none;
  }
`;

const AssigneeSection = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.resolve("spacing_xs")};
  align-items: flex-start;
`;

const AssigneeInfo = styled.div`
  font-family: ${({ theme }) => theme.resolve("font_family_primary")};
  font-size: ${({ theme }) => theme.resolve("font_size_sm")};
  line-height: ${({ theme }) => theme.resolve("line_height_sm")};
  color: ${({ theme }) => theme.resolve("color_text_primary")};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.resolve("spacing_xs")};
`;

const BrandText = styled.span`
  color: ${({ theme }) => theme.resolve("color_text_brand")};
`;

const EditButtonContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 100px;
  background-color: ${({ theme }) => theme.resolve("color_bg_surface_subtle")};
  cursor: pointer;
  margin-top: -3px;
`;

const DateInfo = styled.div`
  font-family: ${({ theme }) => theme.resolve("font_family_primary")};
  font-size: ${({ theme }) => theme.resolve("font_size_sm")};
  line-height: ${({ theme }) => theme.resolve("line_height_sm")};
  color: ${({ theme }) => theme.resolve("color_text_primary")};
  width: 100%;
`;

const SubscriberSection = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.resolve("spacing_xs")};
  align-items: flex-start;
`;

const SubscriberInfo = styled.div`
  font-family: ${({ theme }) => theme.resolve("font_family_primary")};
  font-size: ${({ theme }) => theme.resolve("font_size_sm")};
  line-height: ${({ theme }) => theme.resolve("line_height_sm")};
  color: ${({ theme }) => theme.resolve("color_text_primary")};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.resolve("spacing_xs")};
`;

const TooltipItem = styled.span`
  display: block;
`;

const StyledClickableLink = styled(ClickableLink)`
  color: ${({ theme }) => theme.resolve("color_text_brand")};
`;

export interface LeadAssignmentInfoProps {
  // Assignment data
  assignee?: any;
  assignmentDate?: string | null;

  // Subscriber data
  subscribers?: Array<LeadAssignee> | null;
  subscribersUpdatedOn?: string | null;
  subscriber_assignment_type?: "new" | "updated";

  // Configuration
  shouldShowAssigneeSection: boolean;

  // Actions
  onOpenLeadAssignmentModal: () => void;
  onOpenSubscribersModal: () => void;

  // State
  isActionDisabled: boolean;
  isModificationDisabled?: boolean;

  // Utility functions
}

export function LeadAssignmentInfo({
  assignee,
  assignmentDate,
  subscribers,
  subscribersUpdatedOn,
  subscriber_assignment_type = "new",
  shouldShowAssigneeSection,
  onOpenLeadAssignmentModal,
  onOpenSubscribersModal,
  isActionDisabled,
  isModificationDisabled = false,
}: LeadAssignmentInfoProps): JSX.Element {
  const { t } = useTranslation();
  const countries = useCountriesList();

  const lead_subscribers = useMemo(() => {
    const subscriber_name = (subscriber: {
      firstname: string;
      lastname: string;
    }) => `${subscriber.firstname} ${subscriber.lastname}`;
    const allSubscribers = subscribers ?? [];
    if (allSubscribers.length <= 2) {
      return allSubscribers.map(subscriber_name).join(", ");
    }
    const firstTwo = allSubscribers.slice(0, 2).map(subscriber_name).join(", ");
    return `${firstTwo} +${allSubscribers.length - 2}`;
  }, [subscribers]);

  return (
    <HeaderContainer>
      <AssignmentContainer>
        <FlexContainer $hasAssignee={!!assignee}>
          {shouldShowAssigneeSection && (
            <AssigneeSection>
              <AssigneeInfo>
                <span>{t("Assigned to: ")}</span>
                {assignee ? (
                  <BrandText>
                    <LeadAssigneeDetails
                      leadAssignee={assignee}
                      countries={countries}
                    />
                  </BrandText>
                ) : (
                  <StyledClickableLink
                    onClick={onOpenLeadAssignmentModal}
                    disabled={isActionDisabled}
                  >
                    {t("Add")}
                  </StyledClickableLink>
                )}
              </AssigneeInfo>
              {assignee && (
                <EditButtonContainer>
                  <EditButton
                    width={16}
                    height={16}
                    onClick={onOpenLeadAssignmentModal}
                    disabled={isActionDisabled}
                  />
                </EditButtonContainer>
              )}
              {assignmentDate && assignee && (
                <DateInfo>
                  {t("Assigned on: ")}
                  {formatDateTime(assignmentDate)}
                </DateInfo>
              )}
            </AssigneeSection>
          )}
        </FlexContainer>

        <div>
          {shouldShowAssigneeSection && (
            <SubscriberSection>
              <SubscriberInfo>
                <span>{t("Lead Subscriber(s): ")}</span>
                {subscribers && subscribers.length > 0 ? (
                  <>
                    <BrandText data-for="lead_subscribers" data-tip>
                      {lead_subscribers}
                    </BrandText>
                    {subscribers.length > 2 && (
                      <ReactTooltip
                        id="lead_subscribers"
                        effect="solid"
                        place="top"
                      >
                        {subscribers.map((subscriber) => (
                          <TooltipItem
                            key={subscriber.firstname + subscriber.lastname}
                          >
                            {convertUserToOption(subscriber).label}
                          </TooltipItem>
                        ))}
                      </ReactTooltip>
                    )}
                  </>
                ) : (
                  <StyledClickableLink
                    onClick={onOpenSubscribersModal}
                    disabled={isModificationDisabled}
                  >
                    {t("Add")}
                  </StyledClickableLink>
                )}
              </SubscriberInfo>
              {subscribers && subscribers.length > 0 && (
                <EditButtonContainer>
                  <EditButton
                    width={16}
                    height={16}
                    onClick={onOpenSubscribersModal}
                    disabled={isModificationDisabled}
                  />
                </EditButtonContainer>
              )}
              {subscribersUpdatedOn && subscribers && subscribers.length > 0 && (
                <DateInfo>
                  {subscriber_assignment_type === "new"
                    ? t("Assigned on: ")
                    : t("Updated on: ")}
                  {formatDateTime(subscribersUpdatedOn)}
                </DateInfo>
              )}
            </SubscriberSection>
          )}
        </div>
      </AssignmentContainer>
    </HeaderContainer>
  );
}
